import { useEffect, useState } from "react";
import { X, Download, Smartphone } from "lucide-react";

/** Bottom-sheet install prompt for Android/desktop Chrome/Edge.
 * Behaviour:
 *  - Waits for the browser's `beforeinstallprompt` event
 *  - Only shows on 2nd+ visit (localStorage visit counter) — no nag on first landing
 *  - Debounces 20s after page load so it never interrupts first read
 *  - Dismissed sheet: stored in localStorage for 30 days
 *  - Also detects iOS Safari and shows the "Add to Home Screen" hint (no auto-prompt exists)
 */
const DISMISS_KEY = "ehi_pwa_dismissed_until";
const VISIT_KEY   = "ehi_pwa_visits";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Increment visit counter (per browser)
    let visits = 1;
    try {
      visits = Number(localStorage.getItem(VISIT_KEY) || "0") + 1;
      localStorage.setItem(VISIT_KEY, String(visits));
    } catch {}

    // Respect prior dismissal
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until && Date.now() < until) return;

    // Already installed? Skip.
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return;

    // Second-visit nudge — never on first visit
    if (visits < 2) return;

    // iOS Safari: no beforeinstallprompt — show a static hint after 20s
    const ua = window.navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      const t = setTimeout(() => setIosHint(true), 20000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Debounce to avoid interrupting first read
      setTimeout(() => setVisible(true), 20000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 30 * 24 * 3600 * 1000));
    setVisible(false); setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setVisible(false);
  };

  if (!visible && !iosHint) return null;

  return (
    <div
      role="dialog"
      aria-label="Install EquipmentHub India"
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-[60] bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-2"
    >
      <div className="h-1 bg-gradient-to-r from-ehi-primary via-ehi-accent to-ehi-primary" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-ehi-primary text-white grid place-items-center shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-sm text-ehi-ink">
              {iosHint ? "Add EquipmentHub to your Home Screen" : "Install EquipmentHub India"}
            </div>
            <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
              {iosHint
                ? "Tap the Share button, then \"Add to Home Screen\" — it opens like a real app."
                : "One tap · works offline · faster reopen · your login stays saved."}
            </div>
          </div>
          <button onClick={dismiss} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" aria-label="Dismiss" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!iosHint && (
          <div className="mt-3 flex items-center justify-end gap-2">
            <button onClick={dismiss} className="text-xs px-3 py-2 rounded border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="pwa-install-later">
              Later
            </button>
            <button
              onClick={install}
              className="text-xs px-3 py-2 rounded bg-ehi-primary text-white hover:bg-ehi-primary/90 font-bold tracking-wide inline-flex items-center gap-1.5"
              data-testid="pwa-install-cta"
            >
              <Download className="w-3.5 h-3.5" /> Install
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Toast shown when a new service-worker version is waiting. */
export function PwaUpdateToast() {
  const [reg, setReg] = useState(null);
  useEffect(() => {
    const h = (e) => setReg(e.detail);
    window.addEventListener("pwa:update", h);
    return () => window.removeEventListener("pwa:update", h);
  }, []);
  if (!reg) return null;
  const apply = () => {
    reg.waiting?.postMessage("SKIP_WAITING");
    setReg(null);
    setTimeout(() => window.location.reload(), 300);
  };
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] bg-slate-900 text-white text-xs rounded-full pl-3 pr-2 py-1.5 shadow-lg border border-white/10 flex items-center gap-2" data-testid="pwa-update-toast">
      <span>New version available</span>
      <button onClick={apply} className="bg-ehi-accent text-white rounded-full px-3 py-1 font-black uppercase tracking-wider">Refresh</button>
    </div>
  );
}
